import { TransfersList } from './TransfersList';
import { FreeTransfersPill } from './FreeTransfersPill';
import { GenerateTransfers } from './GenerateTransfers';
import { TransfersMessage } from './TransfersMessage';
import {
  priceDelta,
  totalPredictedPointsGain
} from '@lib/transfers/utils/predicted-points';
import {
  deriveTransfersViewState,
  type TransfersCardMode
} from '@lib/transfers/utils/transfers-view-state';
import { useCustomTransfersStore } from '@lib/transfers/store/useCustomTransfersStore';
import { useRecommendedTransfersStore } from '@lib/transfers/store/useRecommendedTransfersStore';
import { useAITransfersStore } from '@lib/transfers/store/useAITransfersStore';
import { resolveActiveProfileLabel } from '@lib/transfers/utils/profile-label';
import { useSolverProfiles } from '@hooks/transfers/useResolvedPreferences';
import type { TransferRecommendation } from '@api-service/recommendations';
import Button from '@components/atoms/button';
import Icon from '@components/atoms/icon';

type TransfersCardProps = {
  mode: TransfersCardMode;
  transfers: TransferRecommendation[];
  isLoading: boolean;
  isError: boolean;
  /**
   * Why the API refused this request, in the member's terms. Present only when
   * the request itself could never be satisfied; a service failure leaves it
   * undefined and keeps the generic error copy.
   */
  refusalDescription?: string;
  hasLoaded: boolean;
  freeTransfers: number;
  remainingTransfers?: number;
  isUnlimited?: boolean;
  selectedIndex?: number;
  onSelect?: (index: number) => void;
  onRegenerate?: () => void;
  onCommitRegenerate?: () => void;
  onMakeTransfers?: () => void;
  isMakingTransfers?: boolean;
  onDismiss?: () => void;
};

/** e.g. "Total gain +13.6 pts · bank +£2.0m" — sums the per-row deltas. */
const TotalsSummary = ({
  transfers
}: {
  transfers: TransferRecommendation[];
}) => {
  const totalPointsGain = totalPredictedPointsGain(transfers);
  const totalBankChange = transfers.reduce(
    (total, transfer) => total - priceDelta(transfer),
    0
  );
  const pointsSign = totalPointsGain >= 0 ? '+' : '−';
  const bankSign = totalBankChange > 0 ? '+' : totalBankChange < 0 ? '−' : '±';

  return (
    <p
      className="mb-0 hidden font-open-sans text-[13px] text-neutral-600 md:block"
      data-cy="transfers-totals"
    >
      Total gain{' '}
      <span
        className={`font-bold tabular-nums ${
          totalPointsGain >= 0 ? 'text-success-600' : 'text-danger-600'
        }`}
      >
        {pointsSign}
        {Math.abs(totalPointsGain).toFixed(1)} pts
      </span>{' '}
      · bank{' '}
      <span className="font-bold tabular-nums text-neutral-800">
        {bankSign}£{Math.abs(totalBankChange).toFixed(1)}m
      </span>
    </p>
  );
};

// A request that can never be satisfied is the member's to fix, so it reuses
// the wording already used when a request returns nothing usable rather than
// the "something went wrong" of a genuine outage.
const REFUSED_TITLE = 'No recommendations';
const FAILED_TITLE = 'Something went wrong';
const FAILED_DESCRIPTION =
  "We couldn't generate transfer recommendations. Please try again later.";

const ErrorState = ({
  refusalDescription,
  onRegenerate,
  onDismiss
}: {
  refusalDescription?: string;
  onRegenerate?: () => void;
  onDismiss?: () => void;
}) => (
  <TransfersMessage
    title={refusalDescription ? REFUSED_TITLE : FAILED_TITLE}
    description={refusalDescription ?? FAILED_DESCRIPTION}
    dataCy={refusalDescription ? 'transfers-refused' : 'transfers-error'}
    action={
      onRegenerate
        ? {
            label: 'Re-generate transfers',
            onClick: onRegenerate,
            variant: 'primary'
          }
        : onDismiss
        ? { label: 'Dismiss suggestions', onClick: onDismiss }
        : undefined
    }
  />
);

type CardHeaderProps = {
  horizonGameweeks: number;
};

/** Card title with the active profile/horizon sub-line. */
const CardHeader = ({ horizonGameweeks }: CardHeaderProps) => {
  const { data } = useSolverProfiles();
  const selectedProfileId = useAITransfersStore(
    (state) => state.selectedProfileId
  );

  const profiles = data?.data ?? [];
  const profileLabel =
    profiles.length > 0
      ? resolveActiveProfileLabel(selectedProfileId, profiles)
      : '';

  const subLineParts = [
    ...(profileLabel ? [`${profileLabel} profile`] : []),
    ...(horizonGameweeks > 0 ? [`${horizonGameweeks} gameweek horizon`] : [])
  ];

  return (
    <header className="flex items-center justify-between gap-4">
      <div className="flex flex-col">
        <h2 className="mb-0 font-sans text-base font-semibold text-neutral-800">
          AI recommendations
        </h2>
        {subLineParts.length > 0 && (
          <span className="mt-0.5 font-open-sans text-xs text-neutral-400">
            {subLineParts.join(', ')}
          </span>
        )}
      </div>
    </header>
  );
};

type ExclusionEntry = {
  id: string;
  displayName: string;
  /** "keep" = dismissed from the sell side, "avoid" = from the buy side. */
  qualifier: 'keep' | 'avoid';
};

const resolveExclusions = (
  transfers: TransferRecommendation[],
  dismissedPlayerIds: string[]
): ExclusionEntry[] =>
  dismissedPlayerIds.flatMap((id): ExclusionEntry[] => {
    for (const transfer of transfers) {
      if (transfer.out.id === id) {
        return [
          {
            id,
            displayName: transfer.out.displayName,
            qualifier: 'keep' as const
          }
        ];
      }
      if (transfer.in.id === id) {
        return [
          {
            id,
            displayName: transfer.in.displayName,
            qualifier: 'avoid' as const
          }
        ];
      }
    }
    return [];
  });

type ExclusionsTrayProps = {
  entries: ExclusionEntry[];
  onRestore: (playerId: string) => void;
  onClearAll: () => void;
};

/** Names every pending exclusion between the list and the footer. */
const ExclusionsTray = ({
  entries,
  onRestore,
  onClearAll
}: ExclusionsTrayProps) => (
  <div
    className="flex flex-col gap-3 rounded-xl bg-neutral-100 px-4 py-3 md:flex-row md:items-center md:justify-between md:gap-4"
    data-cy="exclusions-tray"
  >
    <div className="flex flex-wrap items-center gap-2.5">
      <span className="font-open-sans text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-600">
        Excluded from the next run
      </span>
      {entries.map((entry) => (
        <span
          key={entry.id}
          className="inline-flex h-7 items-center gap-[7px] whitespace-nowrap rounded-full bg-white pl-2.5 pr-1.5 ring-1 ring-inset ring-neutral-200"
          data-cy="exclusion-chip"
        >
          <span className="font-open-sans text-xs font-semibold text-neutral-800">
            {entry.displayName}
          </span>
          <span className="font-open-sans text-xs text-neutral-500">
            {entry.qualifier}
          </span>
          <button
            type="button"
            onClick={() => onRestore(entry.id)}
            aria-label={`Restore ${entry.displayName}`}
            className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-neutral-200 text-neutral-600 transition-colors duration-150 hover:bg-neutral-300"
            data-cy="exclusion-chip-remove"
          >
            <Icon name="Close" fontSize="10px" />
          </button>
        </span>
      ))}
    </div>
    <button
      type="button"
      onClick={onClearAll}
      className="self-start whitespace-nowrap font-open-sans text-xs font-bold text-primary-600 transition-colors duration-150 hover:text-primary-800 md:self-auto"
      data-cy="clear-exclusions"
    >
      Clear all
    </button>
  </div>
);

const TransfersCardContent = (props: TransfersCardProps) => {
  const recommendedRestorePlayer = useRecommendedTransfersStore(
    (state) => state.restorePlayer
  );
  const customRestorePlayer = useCustomTransfersStore(
    (state) => state.restorePlayer
  );
  const recommendedDismissedIds = useRecommendedTransfersStore(
    (state) => state.disabledPlayerIds
  );
  const customDismissedIds = useCustomTransfersStore(
    (state) => state.disabledPlayerIds
  );

  const isCustomMode = props.mode === 'custom';
  const dismissedPlayerIds = isCustomMode
    ? customDismissedIds
    : recommendedDismissedIds;
  const restorePlayer = isCustomMode
    ? customRestorePlayer
    : recommendedRestorePlayer;

  const hasPendingChanges = dismissedPlayerIds.length > 0;

  const state = deriveTransfersViewState({
    isLoading: props.isLoading,
    isError: props.isError,
    hasLoaded: props.hasLoaded,
    hasTransfers: props.transfers.length > 0,
    freeTransfers: props.freeTransfers,
    remainingTransfers: props.remainingTransfers,
    isUnlimited: props.isUnlimited,
    mode: props.mode
  });

  switch (state) {
    case 'loading':
      return <GenerateTransfers />;

    case 'error':
      return (
        <ErrorState
          refusalDescription={props.refusalDescription}
          onRegenerate={props.onRegenerate}
          onDismiss={props.onDismiss}
        />
      );

    case 'noFreeTransfers':
      return (
        <TransfersMessage
          title="Transfers already made"
          description="Nothing else for you to do!"
          dataCy="transfers-already-made"
        />
      );

    case 'adjustFilters':
      return (
        <TransfersMessage
          title="No recommendations"
          description="We couldn't recommend any transfers based on your criteria. Adjust your filters and try again."
          dataCy="transfers-adjust-filters"
          action={
            props.onDismiss
              ? { label: 'Adjust filters', onClick: props.onDismiss }
              : undefined
          }
        />
      );

    case 'empty':
      return (
        <TransfersMessage
          title="No transfers recommended"
          description="We believe it's best to not make any transfer this week, to save your free transfers for the future."
          dataCy="transfers-empty"
          action={
            props.onDismiss
              ? { label: 'Dismiss suggestions', onClick: props.onDismiss }
              : undefined
          }
        />
      );

    case 'transfers': {
      const shouldShowRegenerate =
        hasPendingChanges && Boolean(props.onCommitRegenerate);
      const exclusionCount = dismissedPlayerIds.length;

      return (
        <>
          <TransfersList
            transfers={props.transfers}
            selectedIndex={props.selectedIndex}
            onSelect={props.onSelect}
            mode={props.mode}
            dismissedPlayerIds={dismissedPlayerIds}
          />
          {hasPendingChanges && (
            <ExclusionsTray
              entries={resolveExclusions(props.transfers, dismissedPlayerIds)}
              onRestore={restorePlayer}
              onClearAll={() =>
                dismissedPlayerIds.forEach((id) => restorePlayer(id))
              }
            />
          )}
          {(props.onMakeTransfers || shouldShowRegenerate) && (
            <footer className="flex flex-col items-stretch gap-4 border-t border-neutral-200 pt-3.5 md:flex-row md:items-center md:justify-between">
              <TotalsSummary transfers={props.transfers} />
              <div className="flex flex-col-reverse gap-2.5 md:flex-row md:items-center">
                {props.onMakeTransfers && (
                  <Button
                    variant={hasPendingChanges ? 'secondary' : 'primary'}
                    className="w-full md:w-auto"
                    onClick={props.onMakeTransfers}
                    loading={props.isMakingTransfers}
                    disabled={hasPendingChanges}
                    dataCy="make-transfers-button"
                  >
                    {`Make ${props.transfers.length} transfer${
                      props.transfers.length === 1 ? '' : 's'
                    }`}
                  </Button>
                )}
                {shouldShowRegenerate && (
                  <Button
                    variant="primary"
                    className="w-full md:w-auto"
                    onClick={props.onCommitRegenerate}
                    dataCy="regenerate-transfers-button"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Icon name="Cached" fontSize="18px" />
                      {`Re-generate with ${exclusionCount} exclusion${
                        exclusionCount === 1 ? '' : 's'
                      }`}
                    </span>
                  </Button>
                )}
              </div>
            </footer>
          )}
        </>
      );
    }
  }
};

export const TransfersCard = ({
  className,
  ...props
}: TransfersCardProps & { className?: string }) => {
  const horizonGameweeks = (props.transfers[0]?.in.fixtures ?? []).length;

  return (
    <section
      // Mobile keeps its horizontal padding tight: the two per-player action
      // chips sit either side of the row's centre gap, and wider padding
      // squeezes them into each other.
      className={`flex flex-col gap-5 rounded-2xl border border-neutral-200 bg-white px-3 py-5 md:p-6 ${
        className ?? ''
      }`}
      data-cy="transfers-card"
    >
      {props.mode === 'recommended' && (
        <CardHeader horizonGameweeks={horizonGameweeks} />
      )}
      <FreeTransfersPill
        freeTransfers={props.freeTransfers}
        remainingTransfers={props.remainingTransfers}
        isUnlimited={props.isUnlimited}
      />
      <TransfersCardContent {...props} />
    </section>
  );
};
